import RichText from 'components/UI/Richtext';
import { ReactElement, Fragment } from 'react';
import { IComponentNumberedListItemFields, IFragmentTextCallout } from 'types/contentful';

interface NumberedListItemsProps extends IComponentNumberedListItemFields {
  siteName: string;
}

export default function NumberedListItems({
  items,
  title,
  siteName,
  description,
  bottomDescription,
  contentAlignment = 'left',
}: NumberedListItemsProps): ReactElement {
  const midPoint = Math.ceil(items.length / 2);

  const titleBorderedHeadingClass =
    contentAlignment === undefined
      ? 'bordered-heading'
      : contentAlignment === 'center'
      ? 'text-center'
      : 'bordered-heading';

  const titleFlexJustifyClass =
    contentAlignment === undefined
      ? 'justify-start'
      : contentAlignment === 'center'
      ? 'justify-center'
      : 'justify-start';

  const richTextAlignClass =
    contentAlignment === undefined
      ? 'text-left'
      : contentAlignment === 'center'
      ? 'text-center'
      : 'text-left';

  // Get content to check if there are contents
  const descriptionBody = (description?.content
    ?.map(
      (block: any) => block?.content?.map((inner: any) => inner.value).join(' ') // eslint-disable-line @typescript-eslint/no-explicit-any
    )
    .join(' ') || '') as string;

  const bottomDescriptionBody = (bottomDescription?.content
    ?.map(
      (block: any) => block?.content?.map((inner: any) => inner.value).join(' ') // eslint-disable-line @typescript-eslint/no-explicit-any
    )
    .join(' ') || '') as string;

  const hasDescription = (): boolean => descriptionBody?.trim()?.length > 0;
  const hasBottomDescription = (): boolean => bottomDescriptionBody?.trim()?.length > 0;

  return (
    <div className="mx-auto ecp-my bg-muted py-10 lg:py-20 px-site-component">
      {title && (
        <div className={`mb-6 flex items-center md:mb-8 ${titleFlexJustifyClass}`}>
          <h3
            className={`${titleBorderedHeadingClass} bh-location-heading-item font-light text-new-34 lg:text-4xl lg:leading-new-43 text-primary-actual`}
          >
            {title.replace('{siteName}', siteName)}
          </h3>
        </div>
      )}

      {hasDescription() && description && (
        <RichText
          document={description}
          classNames={`text-lg text-primary-actual font-light richtext-font-inherit mb-6 md:mb-8 ${richTextAlignClass}`}
        />
      )}

      {items.length > 0 && (
        <>
          {/* Mobile: Single column, sequential order */}
          <div className="grid grid-cols-1 gap-3 md:hidden">
            {items.map((item: IFragmentTextCallout, index: number) => (
              <StepCard key={item.sys.id} index={index + 1} item={item} />
            ))}
          </div>

          {/* Desktop: Two columns, paired layout */}
          <div className="hidden md:grid grid-cols-2 gap-3 gap-x-5 items-stretch">
            {Array.from({ length: midPoint }).map((_, i) => {
              const left = items[i];
              const right = items[i + midPoint];

              return (
                <Fragment key={`pair-${i}`}>
                  {/* Left item */}
                  {left && <StepCard key={left.sys.id} index={i + 1} item={left} />}

                  {/* Right item */}
                  {right && <StepCard key={right.sys.id} index={i + 1 + midPoint} item={right} />}
                </Fragment>
              );
            })}
          </div>
        </>
      )}

      {hasBottomDescription() && bottomDescription && (
        <RichText
          document={bottomDescription}
          classNames={`text-lg text-primary-actual font-light richtext-font-inherit mt-6 md:mt-8 ${richTextAlignClass}`}
        />
      )}
    </div>
  );
}

function StepCard({ index, item }: { index: number; item: IFragmentTextCallout }): ReactElement {
  return (
    <div className="h-full bg-white px-4 py-4 md:px-5">
      <div className="flex items-start gap-4">
        <div className="flex-shrink-0">
          <div className="flex h-10 w-10 items-center justify-center rounded-md bg-tertiary">
            <span className="text-white text-lg font-bold">{index}</span>
          </div>
        </div>
        <div className="min-w-0">
          <h3 className="mt-1 text-lg font-bold text-primary-actual">{item.fields.heading}</h3>
          <p className="mb-0 mt-2 text-lg text-primary-actual">{item.fields.copy}</p>
        </div>
      </div>
    </div>
  );
}
